import * as THREE from "three";

/** Meshes present in the source GLBs that we never want to render. */
const DROPPED_MESH_NAMES = new Set(["Text__0"]);

/**
 * Measures a raw glasses scene and returns the Y rotation (radians) that brings
 * it into the canonical orientation: width on X, temples trailing to -Z, front
 * facing +Z.
 *
 * Works for any model regardless of which axes the author used — no per-model
 * calibration needed.
 */
function autoDetectRotationY(source) {
  const box = new THREE.Box3().setFromObject(source);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());

  // For glasses, width (ear-to-ear) is always the longest horizontal extent.
  if (size.z > size.x) {
    // Width is on Z. Rotate ±90° about Y to bring it to X.
    // After -π/2: Z→-X, X→+Z.  new_center_z = center.x
    // After +π/2: Z→+X, X→-Z.  new_center_z = -center.x
    // We want temples at -Z (new_center_z < 0).
    return center.x <= 0 ? -Math.PI / 2 : Math.PI / 2;
  }

  // Width is already on X.  Temples should trail to -Z; if the bounding-box
  // centre sits at +Z the model is facing backwards — flip 180°.
  return center.z > 0 ? Math.PI : 0;
}

/**
 * Puts an arbitrary glasses GLB into a single predictable coordinate system:
 *
 *   - width  along +X, exactly 1 unit wide ("one frame-width")
 *   - up     along +Y
 *   - front  facing +Z, temples trailing off to -Z
 *   - origin at the centre of the frame front, i.e. the bridge of the nose
 *
 * Once normalized, any model can be dropped onto a face by scaling it to the
 * measured face width — no per-model fudging at the call site.
 *
 * This matters because the source models are wildly inconsistent:
 * glasses-1 is 12.7 units wide with its width on Z, glasses-2 is 0.135 units
 * wide with its width on X, and glasses-3 is 0.36 units wide on X.
 * Calibration supplies the rotation; everything else is measured from geometry.
 */
export function normalizeGlasses(source, calibration) {
  const model = source.clone(true);

  // Strip decorative meshes (glasses-1 carries a floating logo).
  for (const child of [...model.children]) {
    pruneDropped(child);
  }

  // Correct the model's authored axes, then bake the rotation in so later
  // measurements are taken in the final orientation.
  // Per-model calibration overrides auto-detection; explicit 0 means "no
 // correction needed" (auto-detection only runs when rotationY is omitted).
 const autoRot = autoDetectRotationY(model);
 const rx = (calibration && calibration.rotationX) || 0;
 const ry =
 calibration && calibration.rotationY !== undefined
 ? calibration.rotationY
 : autoRot;
 const rz = (calibration && calibration.rotationZ) || 0;
 model.rotation.set(rx, ry, rz);
  model.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());

  // Guard against a degenerate/empty model producing Infinity scales.
  const width = size.x > 1e-6 ? size.x : 1;
  const scale = 1 / width;

  // Inner group carries the centring; the outer group carries the scale, so
  // the caller can read/modify either without them fighting.
  const centered = new THREE.Group();
  centered.add(model);
  // Centre on X (symmetry axis) and Y, but anchor Z at the *front* face of the
  // frame so the origin lands on the bridge rather than midway down the temples.
  centered.position.set(-center.x, -center.y, -box.max.z);

  const normalized = new THREE.Group();
  normalized.add(centered);
  normalized.scale.setScalar(scale);
  normalized.name = "normalized-glasses";

  return normalized;
}

function pruneDropped(object) {
  for (const child of [...object.children]) {
    pruneDropped(child);
  }
  if (DROPPED_MESH_NAMES.has(object.name)) {
    object.removeFromParent();
  }
}
